import InboxRuleRow from './InboxRuleRow';
import InboxRulesEdit from './InboxRulesEdit';
import generateInboxRuleDiagnosticReport from '../actions/generateInboxRulesDiagnosticReport';
import getInboxRules from '../actions/getInboxRules';
import initializeInboxRulesFolders from '../actions/helpers/initializeInboxRulesFolders';
import isInboxRuleOptionEditState from '../actions/helpers/isInboxRuleOptionEditState';
import setInboxRuleDiagnosticRequestStatus from '../actions/helpers/setInboxRuleDiagnosticRequestStatus';
import initializeInboxRuleEdit from '../actions/initializeInboxRuleEdit';
import InboxRuleRequestStatus from '../store/schema/InboxRuleRequestStatus';
import { InboxRulesListOptionState } from '../store/schema/InboxRulesListOptionState';
import store from '../store/store';
import { DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { ActionButton } from 'office-ui-fabric-react/lib/Button';
import { MessageBar, MessageBarType } from 'office-ui-fabric-react/lib/MessageBar';
import { Spinner, SpinnerType } from 'office-ui-fabric-react/lib/Spinner';
import { ControlIcons } from 'owa-control-icons';
import { OptionDescription } from 'owa-options-controls';
import strings from 'owa-strings/owa-options-strings';
import * as React from 'react';
import { reactive } from 'satcheljs/lib/legacy/react';

const styles = require('./InboxRulesFullCSS.scss');
const className = require('classnames/bind').bind(styles);

export interface InboxRulesFullProps {
    inboxRulesListState?: InboxRulesListOptionState;
}

@reactive({
    inboxRulesListState: () => store.inboxRulesListState,
})
export default class InboxRulesFull extends React.Component<InboxRulesFullProps, {}> {
    constructor(props: InboxRulesFullProps) {
        super(props);
        setInboxRuleDiagnosticRequestStatus(InboxRuleRequestStatus.NotInitiated);
        initializeInboxRulesFolders();
    }

    componentDidMount() {
        getInboxRules();
    }

    render() {
        return isInboxRuleOptionEditState()
            ? this.renderInboxRulesEditPage()
            : this.renderRuleList();
    }

    /**
     * Gives back the list of rows depending on the status of getting inbox rules
     * @return inbox rule list
     */
    private renderRuleList(): JSX.Element {
        let getInboxRuleRequestStatus = this.props.inboxRulesListState.getInboxRuleRequestStatus;
        return (
            <>
                <OptionDescription>{strings.options_inboxRules_description}</OptionDescription>
                <div className={styles.actionLayer}>
                    <ActionButton
                        className={styles.ruleCommandButton}
                        onClick={() => initializeInboxRuleEdit()}
                        text={strings.options_inboxRules_addNewRule}
                        iconProps={{
                            iconName: ControlIcons.Add,
                            styles: {
                                root: className(styles.icon),
                            },
                        }}
                    />
                </div>
                {this.getInboxRulesListContent(getInboxRuleRequestStatus)}
            </>
        );
    }

    private getInboxRulesListContent(
        getInboxRuleRequestStatus: InboxRuleRequestStatus
    ): JSX.Element {
        switch (getInboxRuleRequestStatus) {
            case InboxRuleRequestStatus.Complete:
                return this.renderRuleRows();
            case InboxRuleRequestStatus.Error:
                return this.getInboxRuleError();
            case InboxRuleRequestStatus.Pending:
            default:
                return <Spinner className={styles.loadingSpinner} type={SpinnerType.large} />;
        }
    }

    /**
     * Creates error message div on get inbox rules error
     * @return error message div
     */
    private getInboxRuleError(): JSX.Element {
        return (
            <MessageBar
                messageBarType={MessageBarType.error}
                isMultiline={false}
                actions={
                    <DefaultButton
                        onClick={() => {
                            getInboxRules();
                        }}>
                        {strings.options_rules_tryAgain}
                    </DefaultButton>
                }>
                {strings.options_rules_loadingRulesError}
            </MessageBar>
        );
    }

    /**
     * Creates the rule rows in inbox rules settings page
     * @return inbox rule rows
     */
    private renderRuleRows(): JSX.Element {
        let rows: JSX.Element[] = [];
        const inboxRuleCollection = this.props.inboxRulesListState.inboxRuleIds;

        inboxRuleCollection.forEach(inboxRuleId => {
            rows.push(<InboxRuleRow key={inboxRuleId} inboxRuleId={inboxRuleId} />);
        });

        return (
            <div className={styles.inboxRuleRowsContainer}>
                {rows.length > 0 ? (
                    rows
                ) : (
                    <OptionDescription className={styles.emptyRules}>
                        {strings.options_rules_noRules}
                    </OptionDescription>
                )}
                {inboxRuleCollection.length > 0 && this.renderDiagnosticButton()}
            </div>
        );
    }

    private renderDiagnosticButton(): JSX.Element {
        switch (this.props.inboxRulesListState.getInboxRuleDiagnosticRequestStatus) {
            case InboxRuleRequestStatus.Pending:
                return (
                    <div className={styles.diagnosticLoadingContainer}>
                        <Spinner className={styles.diagnosticSpinner} type={SpinnerType.normal} />
                        <span className={styles.diagnosticLoadingText}>
                            {strings.options_inboxRules_generateReportPending}
                        </span>
                    </div>
                );
            case InboxRuleRequestStatus.Error:
                return (
                    <span className={styles.diagnosticText}>
                        {strings.options_inboxRules_generateReportFail}
                    </span>
                );
            case InboxRuleRequestStatus.Complete:
                return (
                    <span className={styles.diagnosticText}>
                        {strings.options_inboxRules_generateReportSuccess}
                    </span>
                );
            default:
                return (
                    <div className={styles.generateReportText}>
                        <ActionButton
                            className={styles.ruleCommandButton}
                            onClick={() => generateInboxRuleDiagnosticReport()}
                            text={strings.options_inboxRules_generateReport}
                        />
                    </div>
                );
        }
    }

    private renderInboxRulesEditPage(): JSX.Element {
        return <InboxRulesEdit />;
    }
}
